import { Button, Col, Row, Spin, Typography } from "antd";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Empty } from "../../../../../assets/svg";
import { Booking } from "../../../../../types/bookings";
import {
  convertToTitleCase,
  formatAmountOrPercentage,
  formatCurrency,
  formatDateRange,
  isCommercialUsageType,
  isResidentialUsageType,
} from "../../../../libs/utils";
import { filterChangeValue } from "../utils";
import { Item } from "../view";

interface IProps {
  booking: Booking;
  newRenewalType?: any;
}

const hasValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") return false;
  if (typeof value === "string" && value.trim() === "") return false;
  return true;
};

const RenewalTerms = (props: IProps) => {
  const navigate = useNavigate();
  const { leaseType, bookingTerms, propertyDetails, leaseId } = props?.booking ?? {};
  const isBooking = props?.booking && Object.keys(props.booking).length > 0;
  const valueStyle = { textTransform: "capitalize" };
  const usagesType = propertyDetails?.usagesType;
  const isCommercial = isCommercialUsageType(usagesType);
  const isResidential = isResidentialUsageType(usagesType);
  const hasRenewal = Boolean(props.booking?.bookingTerms?.renewalAmount) && !props?.newRenewalType;
  const showEmptyState = (isBooking && !props.booking?.bookingTerms?.renewalAmount) || props?.newRenewalType;
  const renewalTerms = props.booking?.renewalTerms;
  const securityDepositTopUp = bookingTerms?.securityDepositTopUp;

  return (
    <Col span={24} className="view-booking">
      {!isBooking && (
        <Row gutter={24} className="loading">
          <Spin size="large" />
        </Row>
      )}

      {isBooking && hasRenewal && (
        <>
          <Row gutter={24} className="bd-item">
            {hasValue(props.booking?.adminManager?.name) && (
              <Item label="Property Admin" value={convertToTitleCase(props.booking?.adminManager?.name)} />
            )}

            {hasValue(leaseType) && <Item label="Type" value={convertToTitleCase(leaseType)} />}

            {(hasValue(props.booking?.leaseTerms?.leaseStartDate) ||
              hasValue(props.booking?.leaseTerms?.leaseEndDate)) && (
              <Item
                label="Renewal Term"
                value={formatDateRange(
                  props.booking.leaseTerms.leaseStartDate,
                  props.booking.leaseTerms.leaseEndDate,
                )}
              />
            )}

            {hasValue(bookingTerms?.numberOfRenewalTerms) && hasValue(bookingTerms?.durationType) && (
              <Item
                label="Renewal Duration"
                value={`${bookingTerms.numberOfRenewalTerms} ${bookingTerms.durationType}`}
              />
            )}

            {hasValue(props.booking?.bookingTerms?.annualBaseRent) && (
              <Item
                label="Current Annual Rent"
                value={formatCurrency(props.booking.bookingTerms.annualBaseRent)}
                renewalAmout={
                  hasValue(renewalTerms?.tempAnnualBaseRent)
                    ? formatCurrency(renewalTerms?.tempAnnualBaseRent)
                    : undefined
                }
              />
            )}

            {hasValue(props.booking?.bookingTerms?.renewalAmount) && (
              <Item
                label="Updated Annual Rent"
                value={formatCurrency(props.booking.bookingTerms.renewalAmount)}
                renewalAmout={
                  hasValue(renewalTerms?.tempRenewalAmount)
                    ? formatCurrency(renewalTerms?.tempRenewalAmount)
                    : undefined
                }
              />
            )}

            {hasValue(props.booking?.bookingTerms?.changeValue) && (
              <Item
                valueStyle={valueStyle}
                label="Change Type"
                value={filterChangeValue(props.booking.bookingTerms?.changeValue ?? "")}
                renewalAmout={
                  hasValue(renewalTerms?.tempChangeValue)
                    ? filterChangeValue(renewalTerms?.tempChangeValue ?? "")
                    : undefined
                }
              />
            )}

            {hasValue(props.booking?.bookingTerms?.amountType) && (
              <Item
                valueStyle={valueStyle}
                label="Adjustment Type"
                value={props.booking.bookingTerms.amountType}
                renewalAmout={renewalTerms?.tempAmountType}
              />
            )}

            {props.booking?.bookingTerms?.changeValue !== "no_change" &&
              hasValue(props.booking?.bookingTerms?.amountChange) && (
                <Item
                  label={`${props.booking.bookingTerms.amountType} of Adjustment`}
                  value={formatAmountOrPercentage(
                    props.booking.bookingTerms.amountChange,
                    props.booking.bookingTerms.amountType,
                  )}
                  renewalAmout={
                    hasValue(renewalTerms?.tempAmountChange) && hasValue(renewalTerms?.tempAmountType)
                      ? formatAmountOrPercentage(
                          renewalTerms?.tempAmountChange,
                          renewalTerms?.tempAmountType,
                        )
                      : undefined
                  }
                  amountOfAdjust={props.booking.bookingTerms.amountChange}
                  renewalAmoutAdjust={renewalTerms?.tempAmountChange}
                  amountTypeCheck={props.booking.bookingTerms.amountType}
                />
              )}

            {hasValue(props.booking?.bookingTerms?.numberOfInstallments) && (
              <Item
                label="No. of Instalments"
                value={props.booking.bookingTerms.numberOfInstallments}
                renewalAmout={renewalTerms?.tempNumberOfInstallments}
              />
            )}

            {leaseType === "extension" && hasValue(bookingTerms?.contractRent) && (
              <Item label="New Contract Rent" value={formatCurrency(bookingTerms.contractRent)} />
            )}

            {(securityDepositTopUp?.requiredAmount ?? 0) > 0 && (
              <>
                {hasValue(securityDepositTopUp?.currentAmount) && (
                  <Item
                    label="Current Deposit Amount"
                    value={formatCurrency(securityDepositTopUp?.currentAmount)}
                  />
                )}
                {hasValue(securityDepositTopUp?.requiredPercentage) && (
                  <Item
                    label="Required Deposit"
                    value={`${securityDepositTopUp?.requiredPercentage}%`}
                  />
                )}
                {hasValue(securityDepositTopUp?.requiredAmount) && (
                  <Item
                    label="Top-Up Amount"
                    value={formatCurrency(securityDepositTopUp?.requiredAmount)}
                  />
                )}
              </>
            )}
          </Row>
        </>
      )}

      {showEmptyState && isCommercial && (
        <Row
          style={{
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: 8,
            padding: "32px 24px",
            textAlign: "center",
          }}
        >
          <Empty width="160px" height="160px" style={{ alignContent: "center" }} />
          <Typography.Title level={5} style={{ margin: "8px 0 0" }}>
            No renewal exists
          </Typography.Title>
          <Typography.Text type="secondary" style={{ maxWidth: 360, marginBottom: 8 }}>
            Start create renewal to configure renewal terms and payment plan.
          </Typography.Text>
          <Button
            type="primary"
            size="large"
            disabled={!leaseId}
            onClick={() => leaseId && navigate(`/renewals/${leaseId}`)}
          >
            Start Renewal
          </Button>
        </Row>
      )}

      {showEmptyState && isResidential && (
        <Row style={{ justifyContent: "center" }}>
          <Empty width="200px" height="200px" style={{ alignContent: "center" }} />
        </Row>
      )}
    </Col>
  );
};

export default RenewalTerms;
